import React from "react";
import { Container, Offcanvas } from "react-bootstrap";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { useAuth } from "../../auth/useAuth";
import { hasValue, processImagePath, setFavIcon, setTitle } from "../../util/Functions";
import { Constant } from "../../util/Constant";

import OrganizationLogoHeader from "./OrganizationLogoHeader";
import CatalogHeader from "./CatalogHeader";
import PrivateLayoutInner from "./PrivateLayoutInner";

import parse from 'html-react-parser';

import {
	BASE_PATH,
	CATALOG_PATHS,
	REACT_APP_PUBLIC_API_KEY,
	REMOTE_BASE_URL,
	SINGLE_PAGE,
	STATIC_CATALOG,
	X_API_KEY
} from "../../constants";

import CatalogContext from "../../contexts/CatalogContext";
import { ProposalContextProvider } from "../../hooks/useProposalContext";
import { ChatContextProvider } from "../../hooks/useChatContext";
import { BookmarkContextProvider } from "../../hooks/useBookmarkContext";
import { ProductDetailContextProvider } from "../../hooks/useProductDetailContext";

import ApiKeyContext from "../../contexts/ApiKeyContext";
import PreviewBanner from "./components/PreviewBanner";
import "./layout.scss";
import LoadMask from "../../components/LoadMask";
import NoCatalogAccess from "./components/NoCatalogAccess";
import NoCatalogPage from "./components/NoCatalogPage";

import SaleReportConversationDialog from "./components/sale_report/SaleReportConversationDialog";
import LiveApiClient from "../../api/LiveApiClient";
import { SidebarContentType, useSidebarContext } from "../../hooks/useSidebarContext";

const NotFound = React.lazy(() => import("../public/pages/NotFound"));
const CustomerSidebar = React.lazy(() => import("../private/components/customer/CustomerSidebar"));
const BookmarkSidebar = React.lazy(() => import("./components/bookmark/BookmarkSidebar"));
const ProposalSidebar = React.lazy(() => import("./components/proposal/ProposalSidebar"));

export default function PrivateLayout() {
	const location = useLocation();
	const search = location.search;
	const queryParams = new URLSearchParams(search);
	const apiContext = React.useContext(ApiKeyContext);
	const { currentUser, organizationMember } = useAuth();

	const [catalog, setCatalog] = React.useContext(CatalogContext);

	const [catalogType, setCatalogType] = React.useState(false);
	const [invitation, setInvitation] = React.useState(false);
	const [isLoadingCatalog, setIsLoadingCatalog] = React.useState(false);
	const SidebarContext = useSidebarContext();

	const currentUserInfo = JSON.parse(localStorage.getItem('organization_member:data'));
	const [hasCatalogAccess, setHasCatalogAccess] = React.useState(true);

	const path = window.location.pathname
		.substring(BASE_PATH ? BASE_PATH.length + 2 : 0)
		.split("/")
		.filter(hasValue);

	Constant.Set('user', currentUser);
	Constant.Set('apiContext', apiContext);
	Constant.Set('catalogContext', apiContext);
	Constant.Set('baseEndpoint', process.env.REACT_APP_API_CLIENT_BASE_URL);
	Constant.Set('cdn', REMOTE_BASE_URL);

	// Add global constants
	window.Constant = Constant;


	const getFetchCatalogHeaders = async () => {
		if (!currentUser) {
			return { 'x-api-key': REACT_APP_PUBLIC_API_KEY };
		}

		// Retrieve api key from invite
		const profile_filter = [
			{ property: "{catalog.uri}", operator: "=", value: "/" + path[0] + "/" + path[1] },
			{ property: "{profile}", operator: "=", value: "@profile_id" },
			{ property: "{is_accepted}", operator: "=", value: "1", },
		];
		const profile_paths = { api_key: true, customer: true, authorized_domains: true }
		const organization_filter = [profile_filter[0]];
		const organization_paths = { invitation: profile_paths };
		const headers = { "api-key": 1, "cache-control": false };

		const profile_promise = LiveApiClient.get(`Catalog_Invitation`, {
			headers,
			params: {
				filter: JSON.stringify(profile_filter),
				paths: JSON.stringify(profile_paths)
			}
		});
		const organization_promise = LiveApiClient.get(`Catalog_Organization_Access`, {
			headers,
			params: {
				filter: JSON.stringify(organization_filter),
				paths: JSON.stringify(organization_paths)
			}
		});

		const [profile, organization] = await Promise.all([profile_promise, organization_promise])
		const invitation = profile.data[0] || organization.data[0]?.invitation;
		apiContext.apiKey = invitation?.api_key || '1';
		apiContext.customer_id = invitation?.customer;
		setInvitation(invitation);
		return { 'api-key': apiContext.apiKey };
	};

	const onBookmarksClear = () => {
		SidebarContext.exit();
	}

	React.useEffect(() => {
		//redirect to login if catalog type isn't set, and user isn't logged in and x-api-key is null.
		//if user isn't logged in, but there's an X_API_KEY, then it's for microsite.
		if ((catalogType !== false) && (!currentUser && (!X_API_KEY))) {
			const redirect = encodeURIComponent(location.pathname + location.search);
			window.location = '/login?redirect=' + redirect;
		}
	}, [
		currentUser,
		catalogType
	]);

	React.useEffect(() => {
		async function fetchCatalog() {
			if (isLoadingCatalog) {
				return;
			}
			setIsLoadingCatalog(true);

			const headers = await getFetchCatalogHeaders();
			const identifier = STATIC_CATALOG ? STATIC_CATALOG.id : ':' + path[0] + ':' + path[1];

			headers['cache-control'] = !localStorage.getItem('nocache') ? 'private' : 'no-cache'
			await LiveApiClient.get('Catalog/' + identifier, {
				headers,
				params: { paths: JSON.stringify(CATALOG_PATHS) }
			}).then((catalog_response) => {
				const catalogData = catalog_response.data;

				setCatalogType(catalogData.options.type);

				apiContext.account = catalogData?.account?.id;
				const branding = catalogData.options?.branding;

				branding && setFavIcon(processImagePath(branding.icon || branding.logo, ""));

				const catalogDataCopy = Object.assign(catalogData);
				if (catalogDataCopy.options.enforce_roles && catalogDataCopy.options.proposals?.access) {
					// Override the proposals access
					const organization_member_data = JSON.parse(localStorage.getItem('organization_member:data')) || organizationMember;
					catalogDataCopy.options.proposals.access = organization_member_data?.role?.access?.proposals;
				}

				setCatalog(catalogDataCopy);
				setIsLoadingCatalog(false);
			}).catch(error => {
				//if there's an error with the process.
				setHasCatalogAccess(false);
			});

		}

		!catalog && fetchCatalog();
	}, [
		catalog,
		setCatalog,
		isLoadingCatalog,
		setIsLoadingCatalog,
		apiContext
	]);

	if (!hasCatalogAccess) {
		return (<NoCatalogAccess />);
	}
	// Don't render page until catalog has been fetched
	if (catalog === null) {
		return (<LoadMask show={true} />);
	}
	if (!catalog.pages?.length) {
		return (<NoCatalogPage />);
	}

	// Check domain
	// FIXME:  Embedded sites do not pull up the invitation, they use the X-API-Key instead
	// const allowedDomains = ['localhost', 'bosslogics.com', 'bosslogics.live']
	// 	.concat(invitation?.authorized_domains || []);
	// const topLevelDomain = getTopLevelDomain(window.location.hostname);

	const uriRoot = STATIC_CATALOG ? '' : catalog?.uri;
	setTitle(
		[catalog.label, catalog.account.name]
			.filter((p) => p)
			.join(" | ")
	);

	const generatePageRoutes = () => {
		const pages = catalog.pages
			.filter(page => page.type?.code !== 'link' && page.status)
			.filter(page => page.type);
		const decodedPages = pages.filter(page => page.slug !== decodeURIComponent(page.slug));

		return pages.map(page =>
			<Route
				key={page.id + page.slug + '-encoded'}
				path={uriRoot + (page.slug)}
				element={React.createElement(PrivateLayoutInner, { ...page, uriRoot })}
			/>
		).concat(
			decodedPages.map(page => (
				<Route
					key={page.id + page.slug + '-decoded'}
					path={uriRoot + decodeURIComponent(page.slug)}
					element={React.createElement(PrivateLayoutInner, { ...page, uriRoot })}
				/>
			))
		);
	}

	// TODO ... this can be fixed by updated initial_url definition in Object Designer
	const initialPage = catalog.pages.filter(it => !it.parent || it.parent === "0").sort((a, b) => a.rank - b.rank).at(0);
	let initialURI = STATIC_CATALOG ? catalog.initial_uri.substring(catalog.uri.length) : (initialPage?.uri ?? catalog.initial_uri);
	if (SINGLE_PAGE) {
		const searchParams = new URLSearchParams(window.location.search);
		const currentLocationParam = searchParams.get('_loc');
		searchParams.set('_loc', initialURI);

		initialURI = window.location.pathname + '?' + searchParams.toString();
		if (!currentLocationParam) {
			window.location = initialURI;
		}
	}

	const PreviewBannerIfNeeded = () => {
		if (localStorage.getItem('nocache')) {
			return (<PreviewBanner></PreviewBanner>);
		}
	}

	const showBranding = currentUserInfo?.organization?.logo || currentUserInfo?.organization?.parent?.logo;

	const replaceScripts = node => {
		if (node.type === 'script') {
			const externalScript = node.attribs.src;
			const script = document.createElement('script');
			if (externalScript) {
				script.src = node.attribs.src;
			}
			else {
				script.innerHTML = node.children[0].data;
			}

			document.head.append(script);
		}
	}

	return (
		<>
			<PreviewBannerIfNeeded />

			<ChatContextProvider>
				<ProposalContextProvider>
					<BookmarkContextProvider>
						<ProductDetailContextProvider>
							<div className="headers">
								{
									currentUser &&
									<OrganizationLogoHeader
										currentUserInfo={currentUserInfo}
									/>
								}

								<CatalogHeader
									uriRoot={uriRoot}
									navRoutes={catalog.pages}
									className={'type-' + catalogType}
									currentUserInfo={currentUserInfo}
									hasBranding={showBranding}
								/>
							</div>
							<main className={'type-' + catalogType}>
								<Container bsPrefix="_b-container" className="main-inner" fluid>
									<React.Suspense fallback={<LoadMask show={true} />}>
										<Routes location={SINGLE_PAGE && decodeURIComponent(queryParams.get('_loc'))}>
											<Route key="path-root" path="/" element={<Navigate to={initialURI} />} />
											<Route key="path-uri-root" path={uriRoot} element={<Navigate to={initialURI} />} />
											{generatePageRoutes()}
											<Route key="path-not-found" path="*" element={<NotFound />} />
										</Routes>
									</React.Suspense>
									{catalog.footer && !catalog.options?.appearance?.footer?.hidden && (<footer>{parse(catalog.footer, {
										replace: node => replaceScripts(node)
									})}</footer>)}
									<SaleReportConversationDialog />
								</Container>

								{SidebarContext.type === SidebarContentType.Bookmark
									? (
										<React.Suspense fallback={<></>}>
											<BookmarkSidebar
												onClear={onBookmarksClear}
											/>
										</React.Suspense>
									)
									: (
										<Offcanvas
											show={SidebarContext.isOpen}
											onHide={() => SidebarContext.close()}
											placement="end"
											enforceFocus={false}
											className="_boss _boss-offcanvas"
											data-dialog-type="Sidebar"
										>
											<Offcanvas.Header closeButton>
												<Offcanvas.Title>
													{catalog.options?.proposals && SidebarContext.type === SidebarContentType.Proposal}
													{/*{sidebarContent === "chat" && (<ChatSidebarTitle />)}*/}
												</Offcanvas.Title>
											</Offcanvas.Header>
											<Offcanvas.Body>
												{/*{sidebarContent === "chat" && <ChatSidebar />}*/}
												{SidebarContext.type === SidebarContentType.Proposal && (
													<React.Suspense fallback={<></>}>
														<ProposalSidebar />
													</React.Suspense>
												)}
												{SidebarContext.type === SidebarContentType.Customer && (
													<React.Suspense fallback={<></>}>
														<CustomerSidebar />
													</React.Suspense>
												)}
											</Offcanvas.Body>
										</Offcanvas>
									)
								}
							</main>
						</ProductDetailContextProvider>
					</BookmarkContextProvider>
				</ProposalContextProvider>
			</ChatContextProvider>
		</>
	);
}
